// Sequencing helpers for the remittance reads (in-memory only, nothing persisted).
//
// 1. STALE FETCH GUARD. A list fetch that STARTED before a local mark / reverse and
//    resolves AFTER it must be discarded, otherwise it could re-mark a just-reversed
//    invoice as paid in the cache. Every local mark / reverse bumps a module-level
//    counter (once before the write and once after it); a fetch takes a ticket
//    before it starts and is stale when the counter moved. A consumer also gets
//    "latest wins": a superseded response of the same consumer is dropped.
// 2. DEBOUNCE. Realtime events arrive in bursts; reloads triggered by them are
//    collapsed into one fetch after a short quiet period.
// 3. LOCAL CHANGE EVENT. After a local mark / reverse every open consumer reloads.

let localSeq = 0;
const localListeners = new Set();

export const REMIT_RELOAD_DEBOUNCE_MS = 500;

/** Call right before a local mark / reverse write and again right after it finished. */
export function noteLocalRemittanceMutation() { localSeq += 1; }

/** Tell every open consumer that a local mark / reverse completed (they reload immediately). */
export function notifyLocalRemittanceChange() {
  localListeners.forEach((fn) => {
    try { fn(); } catch (e) { console.warn("[remittanceSync] local change listener threw:", e?.message || e); }
  });
}

export function subscribeLocalRemittanceChange(fn) {
  localListeners.add(fn);
  return () => { localListeners.delete(fn); };
}

/**
 * One gate per consumer. begin() before starting a fetch; isCurrent(ticket) once
 * it resolved: false when a newer fetch of this consumer started, or a local
 * mark / reverse happened since (the response may pre-date it).
 */
export function createFetchGate() {
  let own = 0;
  return {
    begin() { own += 1; return { own, local: localSeq }; },
    isCurrent(t) { return !!t && t.own === own && t.local === localSeq; },
    // Stale ONLY because a local mutation happened (not because a newer fetch superseded it).
    staleByLocalMutation(t) { return !!t && t.own === own && t.local !== localSeq; },
  };
}

/** Collapse a burst of triggers into one call after ms of quiet. cancel() drops a pending call. */
export function createDebounced(fn, ms) {
  let timer = null;
  const wait = ms === undefined ? REMIT_RELOAD_DEBOUNCE_MS : ms;
  return {
    trigger() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; fn(); }, wait);
    },
    cancel() { if (timer) { clearTimeout(timer); timer = null; } },
  };
}

// Test hook only.
export function _resetRemittanceSyncForTests() { localSeq = 0; localListeners.clear(); }
